import { Injectable } from '@angular/core';
import {
  codeDeliveryDestination,
  credentials,
  isInvitationValid,
  selectError,
  selectHasLoadTokenFromLocaleStorageCompleted,
  selectIsAuthenticated,
  selectIsLoading,
  selectShowSuccessMessage,
  selectSsoLoginUrl,
  selectToken,
  sessionId,
} from '../selectors/auth.selectors';
import { Store } from '@ngrx/store';
import { AuthenticateState } from '../reducers/auth.reducer';
import { AcceptInvitationRequest, Credentials, SignupInformation } from '../../models';
import {
  activateActions,
  changePasswordActions,
  impersonate,
  loginActions,
  logoutActions,
  messageHandlingActions,
  registerActions,
  requestPasswordActions,
  resendCodeActions,
  resetPasswordActions,
  setupMFAActions,
  smsChallengeActions,
  ssoActions,
  invitationActions,
  userSessionActions,
} from '../actions/auth.actions';

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  token$ = this.store.select(selectToken);
  error$ = this.store.select(selectError);
  isLoading$ = this.store.select(selectIsLoading);
  isAuthenticated$ = this.store.select(selectIsAuthenticated);
  codeDeliveryDestination$ = this.store.select(codeDeliveryDestination);
  sessionId$ = this.store.select(sessionId);
  credentials$ = this.store.select(credentials);
  hasLoadTokenFromLocaleStorageCompleted$ = this.store.select(selectHasLoadTokenFromLocaleStorageCompleted);
  showSuccessMessage$ = this.store.select(selectShowSuccessMessage);
  ssoLoginUrl$ = this.store.select(selectSsoLoginUrl);
  isInvitationValid$ = this.store.select(isInvitationValid);

  constructor(private store: Store<AuthenticateState>) {}

  login(credentials: Credentials): void {
    this.store.dispatch(loginActions.login({ credentials }));
  }

  loginWithSSo(authCode: string, websiteId: number): void {
    this.store.dispatch(ssoActions.loginWithSso({ authCode, websiteId }));
  }

  logout(): void {
    this.store.dispatch(logoutActions.logout());
  }

  logoutAuthGuard(redirectUrl: string = ''): void {
    this.store.dispatch(logoutActions.authguard({ redirectUrl }));
  }

  clearUserSession(): void {
    this.store.dispatch(userSessionActions.clearsession());
  }

  impersonate(): void {
    this.store.dispatch(impersonate());
  }

  requestPassword(email: string): void {
    this.store.dispatch(requestPasswordActions.request({ email }));
  }

  resetPassword(confirmationCode: string, password: string): void {
    this.store.dispatch(resetPasswordActions.reset({ password, confirmationCode }));
  }

  changePassword(currentPassword: string, password: string): void {
    this.store.dispatch(changePasswordActions.change({ currentPassword, password }));
  }

  forceChangePassword(): void {
    this.store.dispatch(changePasswordActions.force());
  }

  register(signupInformation: SignupInformation): void {
    this.store.dispatch(registerActions.register({ signupInformation }));
  }

  activateAccount(): void {
    this.store.dispatch(activateActions.activate());
  }

  resendCode(): void {
    this.store.dispatch(resendCodeActions.resend());
  }

  resetUsersData(): void {
    this.store.dispatch(smsChallengeActions.reset());
  }

  hideSuccessMessage(): void {
    this.store.dispatch(messageHandlingActions.hideSuccess({ message: null }));
  }

  hideErrorMessage(): void {
    this.store.dispatch(messageHandlingActions.hideError());
  }

  confirmCode(code: string): void {
    this.store.dispatch(smsChallengeActions.confirm({ code }));
  }

  setupMfa(phoneNumber: string): void {
    this.store.dispatch(setupMFAActions.confirm({ phoneNumber }));
  }

  loadSsoLogin(websiteId: number, websiteUrl: string): void {
    this.store.dispatch(ssoActions.loadSsoLogin({ websiteId, websiteUrl }));
  }

  goToIdentityProvider(): void {
    this.store.dispatch(ssoActions.goToIdentityProvider());
  }

  ssoError(error: Error): void {
    this.store.dispatch(ssoActions.loginWithSsoFailed({ error }));
  }

  validateInvitation(invitation: string): void {
    this.store.dispatch(invitationActions.validateInvitation({ invitation }));
  }

  acceptInvitation(acceptInvitation: AcceptInvitationRequest): void {
    this.store.dispatch(invitationActions.acceptInvitation({ acceptInvitation }));
  }
}
